/**
 * BL-1910 — Edit lots' pure summary and copy: folds each lot's rule-book answer
 * into the per-field refusals the form shows inline and the review counts, and
 * words the form's footer, review lines and warning note. Pure and client-safe
 * (ADR 010 — Isomorphic Services); the server summarizes, the form words.
 */
import type { LockReason, LotPhaseKind } from '@auctogon/core/isomorphic/lifecycle';
import type { MoneyWire } from '@auctogon/core/money';
import type { EditLotsFieldKey, EditLotsFields, EditLotsTitledOnlyField } from '@auctogon/core-admin/schemas/lot-batch';
import type { EditLotsReview } from '@auctogon/core-admin/schemas/responses/lot-batch';
import { HOUSE_SALES_REP_LABEL } from '@auctogon/core-admin/services/sales-rep';
import { isLandCategory } from '@auctogon/core-admin/item-form';

import { PRICE_CURRENCY_MISMATCH_MESSAGE, PRICING_BASIS_MIXED_MESSAGE } from './lot-pricing-basis';
import { joinWithAnd, nameLots } from './lot-number-names';

/** Why one lot refuses one field: a rule-book lock, or the per-acre acreage rule. */
export type EditLotsRefusalReason = LockReason | 'acreage';

/** One lot's answer to the requested changes — the server builds these under the lock. */
export interface EditLotsLotVerdict {
  /** The lot's auction-aware phase, or null when none derives (a non-terminal Live Webcast lot). */
  phaseKind: LotPhaseKind | null;
  refused: Array<{ field: EditLotsFieldKey; reason: EditLotsRefusalReason }>;
  /** A chosen field saves with a caution on this lot (the signed map's ⚠ cells or the Admin override). */
  cautioned: boolean;
  /** An Admin's permission cleared this lot's live bid-ladder lock on a chosen field. */
  overridden: boolean;
  /**
   * BL-2146 (PRJ-75 sweep P3, P3-N2) — this lot's printed number (or null,
   * unnumbered), so a refusal can name the blocking lot (ADR 026 — Server
   * Orchestration) instead of only a count out of the selection.
   */
  lotNumber: string | null;
}

/** Plain-words noun per field, for refusal sentences ("its seller can’t change"). */
const FIELD_NOUN: Record<EditLotsFieldKey, string> = {
  categoryId: 'category',
  sellerId: 'seller',
  locationId: 'item location',
  isTitledItem: 'titled setting',
  // BL-2320
  titleStatus: 'title status',
  // BL-2396
  titleMailingStatus: 'title mailing status',
  taxStatus: 'tax status',
  taxRateId: 'tax rate',
  // BL-1912
  salesRepId: 'sales rep',
  startPrice: 'starting price',
  reserve: 'reserve',
  // BL-2775
  showSeller: 'seller contact',
  showSalesRep: 'sales rep contact',
};

/** The tax pair, whose sold-lot refusal points at the invoice (BL-1904's lot panel hint). */
const TAX_FIELDS: ReadonlySet<EditLotsFieldKey> = new Set(['taxStatus', 'taxRateId']);

/** Phases in a stable order, so equal counts always list the same way. */
const PHASE_ORDER: readonly LotPhaseKind[] = [
  'open-for-bidding',
  'last-call',
  'awaiting-resolution',
  'paused',
  'pre-bidding',
];

/** How the warning names each bidder-visible phase ("2 lots are already accepting bids"). */
const PHASE_PHRASE: Partial<Record<LotPhaseKind, string>> = {
  'pre-bidding': 'accepting pre-bids',
  'open-for-bidding': 'accepting bids',
  'last-call': 'in last call',
  'awaiting-resolution': 'awaiting a result',
  paused: 'paused',
};

/**
 * BL-2146 (PRJ-75 sweep P3, P3-N2) — the refusal's lead noun phrase: the
 * blocking lot(s) by number when any are known, falling back to the plain
 * count (ADR 026 — Server Orchestration: name the blocking lots). Up to 3
 * numbers are named directly; a bigger block keeps the count and names the
 * first 3 ("… including Lot 7, Lot 12, Lot 19 and 2 more").
 *
 * @param opts.count Lots refusing for this reason.
 * @param opts.total Lots selected.
 * @param opts.lotNumbers This reason's blocking lots' printed numbers (null for unnumbered).
 * @returns The lead phrase, e.g. "Lot 7" or "5 of 50 selected lots, including Lot 7, Lot 12, Lot 19 and 2 more".
 * @sideEffect none — pure.
 */
function refusalLead(opts: { count: number; total: number; lotNumbers: ReadonlyArray<string | null> }): string {
  const { count, total, lotNumbers } = opts;
  const generic = `${count} of ${total} selected lots`;
  const names = nameLots({ lotNumbers: lotNumbers.filter((n): n is string => n != null) });
  if (names == null) return generic;
  if (count === 1) return names;
  return `${generic}, including ${names}`;
}

/**
 * One refusal sentence: how many lots, and why, in plain words — e.g.
 * "Lot 7 is sold — its seller can’t change".
 *
 * @param opts.field The refused field.
 * @param opts.reason Why.
 * @param opts.count Lots refusing for this reason.
 * @param opts.total Lots selected.
 * @param opts.lotNumbers This reason's blocking lots' printed numbers (null for unnumbered).
 * @returns The sentence.
 * @sideEffect none — pure.
 */
export function editLotsRefusalSentence(opts: {
  field: EditLotsFieldKey;
  reason: EditLotsRefusalReason;
  count: number;
  total: number;
  lotNumbers: ReadonlyArray<string | null>;
}): string {
  const { field, reason, count, total, lotNumbers } = opts;
  const one = count === 1;
  const lead = refusalLead({ count, total, lotNumbers });
  const noun = FIELD_NOUN[field];
  const its = one ? 'its' : 'their';
  if (reason === 'acreage') return `${lead} can’t take this category`;
  if (reason === 'sold-lock' && TAX_FIELDS.has(field)) {
    return `${lead} ${one ? 'is' : 'are'} sold — change ${its} tax on the invoice`;
  }
  if (reason === 'sold-lock') return `${lead} ${one ? 'is' : 'are'} sold — ${its} ${noun} can’t change`;
  if (reason === 'active-lock') return `${lead} ${one ? 'has' : 'have'} live bids — ${its} ${noun} can’t change`;
  return `${lead} can’t change ${noun} right now`;
}

/**
 * Fold every lot's verdict into the form's inline refusals and the review counts.
 *
 * @param opts.verdicts One verdict per selected lot.
 * @param opts.acreageMessage The per-acre rule's own sentence, appended to an acreage refusal.
 * @returns `fieldErrors` (empty when nothing refuses) and the review's warning counts.
 * @sideEffect none — pure.
 */
export function summarizeEditLots(opts: {
  verdicts: readonly EditLotsLotVerdict[];
  acreageMessage: string;
}): { fieldErrors: Partial<Record<EditLotsFieldKey, string>>; warnings: EditLotsReview['warnings'] } {
  const { verdicts, acreageMessage } = opts;
  const total = verdicts.length;
  const refusalCounts = new Map<
    string,
    { field: EditLotsFieldKey; reason: EditLotsRefusalReason; count: number; lotNumbers: Array<string | null> }
  >();
  for (const v of verdicts) {
    for (const r of v.refused) {
      const key = `${r.field}:${r.reason}`;
      const entry = refusalCounts.get(key) ?? { ...r, count: 0, lotNumbers: [] };
      entry.count += 1;
      entry.lotNumbers.push(v.lotNumber);
      refusalCounts.set(key, entry);
    }
  }
  const fieldErrors: Partial<Record<EditLotsFieldKey, string>> = {};
  for (const { field, reason, count, lotNumbers } of refusalCounts.values()) {
    const sentence = editLotsRefusalSentence({ field, reason, count, total, lotNumbers });
    const full = reason === 'acreage' ? `${sentence} — ${acreageMessage}` : sentence;
    fieldErrors[field] = fieldErrors[field] ? `${fieldErrors[field]}; ${full}` : full;
  }
  return { fieldErrors, warnings: countWarnings(verdicts) };
}

/**
 * The review's warning counts: cautioned lots by phase (largest first), and
 * overridden lots with bids.
 *
 * @param verdicts One verdict per selected lot.
 * @returns The warning counts.
 * @sideEffect none — pure.
 */
function countWarnings(verdicts: readonly EditLotsLotVerdict[]): EditLotsReview['warnings'] {
  const byPhase = new Map<LotPhaseKind, number>();
  let withBids = 0;
  for (const v of verdicts) {
    if (v.overridden) withBids += 1;
    if (v.cautioned && v.phaseKind != null) byPhase.set(v.phaseKind, (byPhase.get(v.phaseKind) ?? 0) + 1);
  }
  const rank = (k: LotPhaseKind) => {
    const i = PHASE_ORDER.indexOf(k);
    return i === -1 ? PHASE_ORDER.length : i;
  };
  return {
    byPhase: [...byPhase.entries()]
      .map(([phaseKind, count]) => ({ phaseKind, count }))
      .sort((a, b) => b.count - a.count || rank(a.phaseKind) - rank(b.phaseKind)),
    withBids,
  };
}

/**
 * The review's amber note (AC 9, AC 21), or null when nothing warns — e.g.
 * "2 lots are already accepting bids and 1 is accepting pre-bids. Are you sure
 * you want to make these changes?". Draft and Upcoming lots never warn, so they
 * are never named.
 *
 * @param warnings The dry run's warning counts.
 * @returns The note, or null.
 * @sideEffect none — pure.
 */
export function formatEditLotsWarning(warnings: EditLotsReview['warnings']): string | null {
  const groups = warnings.byPhase.map(({ phaseKind, count }, i) => {
    const phrase = PHASE_PHRASE[phaseKind] ?? 'open to bidders';
    if (i === 0) return `${count} ${count === 1 ? 'lot is' : 'lots are'} already ${phrase}`;
    return `${count} ${count === 1 ? 'is' : 'are'} ${phrase}`;
  });
  const sentences: string[] = [];
  if (groups.length > 0) sentences.push(`${joinWithAnd(groups)}.`);
  if (warnings.withBids > 0) {
    sentences.push(`${warnings.withBids} ${warnings.withBids === 1 ? 'lot has' : 'lots have'} bids.`);
  }
  if (sentences.length === 0) return null;
  return `${sentences.join(' ')} Are you sure you want to make these changes?`;
}

/**
 * The form footer's count line: "No changes yet", or "1 field set · 3 lots selected".
 *
 * @param opts.fieldsSet How many fields are set.
 * @param opts.lotCount How many lots are selected.
 * @returns The line.
 * @sideEffect none — pure.
 */
export function formatEditLotsFooter(opts: { fieldsSet: number; lotCount: number }): string {
  const { fieldsSet, lotCount } = opts;
  if (fieldsSet === 0) return 'No changes yet';
  return `${fieldsSet} ${fieldsSet === 1 ? 'field' : 'fields'} set · ${lotCount} ${lotCount === 1 ? 'lot' : 'lots'} selected`;
}

/**
 * The success toast's headline (AC 10): "2 fields updated" — the bulk bar
 * appends " · 3 lots".
 *
 * @param fieldsSet How many fields the save set.
 * @returns The headline.
 * @sideEffect none — pure.
 */
export function formatEditLotsUpdated(fieldsSet: number): string {
  return `${fieldsSet} ${fieldsSet === 1 ? 'field' : 'fields'} updated`;
}

/** The dry run's selection counts the tax helpers read. */
type EditLotsTaxCounts = Pick<EditLotsReview, 'lotCount' | 'taxExemptLotCount'>;

/**
 * Whether a save skips the Tax Exempt lots: it sets Tax Rate and leaves Tax
 * Status unchanged, so an exempt lot keeps no rate (BL-1913 AC 4).
 *
 * @param setFields The set fields.
 * @returns True when the rate skips exempt lots.
 * @sideEffect none — pure.
 */
function rateSkipsExemptLots(setFields: readonly EditLotsFieldKey[]): boolean {
  return setFields.includes('taxRateId') && !setFields.includes('taxStatus');
}

/**
 * The review's reach suffix for Tax Rate (BL-1913 AC 4) — "applies to 2 of 3
 * lots (1 is tax exempt)" — or null when every selected lot takes the rate.
 *
 * @param opts.setFields The set fields.
 * @param opts.review The dry run's counts.
 * @returns The suffix, or null.
 * @sideEffect none — pure.
 */
export function formatEditLotsTaxRateReach(opts: {
  setFields: readonly EditLotsFieldKey[];
  review: EditLotsTaxCounts;
}): string | null {
  const { setFields, review } = opts;
  const exempt = review.taxExemptLotCount;
  if (!rateSkipsExemptLots(setFields) || exempt === 0) return null;
  return `applies to ${review.lotCount - exempt} of ${review.lotCount} lots (${exempt} ${exempt === 1 ? 'is' : 'are'} tax exempt)`;
}

/**
 * BL-2775 AC 2 — a Show flag's review line: how many lots the save changes, and how
 * many already hold the chosen value. Every count is said, zero included.
 *
 * @param opts.field The flag.
 * @param opts.value The chosen value: true is Show, false is Hide.
 * @param opts.review The dry run's counts.
 * @returns e.g. "3 lots change, 2 already shown".
 * @sideEffect none — pure.
 */
export function formatEditLotsShowFlagReach(opts: {
  field: 'showSeller' | 'showSalesRep';
  value: boolean;
  review: Pick<EditLotsReview, 'lotCount' | 'showSellerLotCount' | 'showSalesRepLotCount'>;
}): string {
  const { field, value, review } = opts;
  const shown = field === 'showSeller' ? review.showSellerLotCount : review.showSalesRepLotCount;
  const already = value ? shown : review.lotCount - shown;
  const changes = review.lotCount - already;
  return `${changes} ${changes === 1 ? 'lot changes' : 'lots change'}, ${already} already ${value ? 'shown' : 'hidden'}`;
}

/**
 * Whether Tax Rate is unused and greyed out (BL-1913 AC 3): the form sets Tax
 * Exempt, or leaves Tax Status unchanged while every selected lot is already
 * Tax Exempt. Before the first dry run lands nothing is known, so it stays usable.
 *
 * @param opts.taxStatus The form's Tax Status (`undefined` = "Leave unchanged").
 * @param opts.review The latest dry run's counts, or null.
 * @returns True when the rate would apply to no lot.
 * @sideEffect none — pure.
 */
export function isEditLotsTaxRateUnused(opts: {
  taxStatus: EditLotsFields['taxStatus'];
  review: EditLotsTaxCounts | null;
}): boolean {
  const { taxStatus, review } = opts;
  if (taxStatus !== undefined) return taxStatus === 'tax_exempt';
  return review != null && review.lotCount > 0 && review.taxExemptLotCount === review.lotCount;
}

/** The dry run's selection counts the titled-only field helpers read. */
type EditLotsTitleCounts = Pick<EditLotsReview, 'lotCount' | 'untitledLotCount'>;

/**
 * BL-2320 / BL-2396 — the review's reach suffix for a titled-only field (Title
 * Status, Title Mailing Status; AC 2) — "applies to 2 of 3 lots (1 isn't a Titled
 * Item)" — or null when every selected lot takes it: the field isn't set, the same
 * save makes every lot titled (AC 4), or every lot already is.
 *
 * @param opts.field The titled-only field the line is for.
 * @param opts.setFields The set fields.
 * @param opts.isTitledItem The form's Titled Item (`undefined` = "Leave unchanged").
 * @param opts.review The dry run's counts.
 * @returns The suffix, or null.
 * @sideEffect none — pure.
 */
export function formatEditLotsTitledFieldReach(opts: {
  field: EditLotsTitledOnlyField;
  setFields: readonly EditLotsFieldKey[];
  isTitledItem: EditLotsFields['isTitledItem'];
  review: EditLotsTitleCounts;
}): string | null {
  const { field, setFields, isTitledItem, review } = opts;
  const untitled = review.untitledLotCount;
  if (!setFields.includes(field) || isTitledItem !== undefined || untitled === 0) return null;
  const skipped = untitled === 1 ? '1 isn’t a titled item' : `${untitled} aren’t titled items`;
  return `applies to ${review.lotCount - untitled} of ${review.lotCount} lots (${skipped})`;
}

/**
 * BL-2320 / BL-2396 — whether the titled-only fields are unused and greyed out
 * (AC 3, AC 5): the form sets "Not titled", or leaves Titled Item unchanged while
 * no selected lot is a Titled Item. "Titled" in the form always makes them usable
 * (AC 4). Before the first dry run lands nothing is known, so they stay usable.
 *
 * @param opts.isTitledItem The form's Titled Item (`undefined` = "Leave unchanged").
 * @param opts.review The latest dry run's counts, or null.
 * @returns True when a titled-only field would apply to no lot.
 * @sideEffect none — pure.
 */
export function isEditLotsTitledFieldUnused(opts: {
  isTitledItem: EditLotsFields['isTitledItem'];
  review: EditLotsTitleCounts | null;
}): boolean {
  const { isTitledItem, review } = opts;
  if (isTitledItem !== undefined) return !isTitledItem;
  return review != null && review.lotCount > 0 && review.untitledLotCount === review.lotCount;
}

/**
 * BL-2591 — whether every lot the save reaches is Land, so Edit lots hides its
 * title rows (Titled Item, Title Status, Title Mailing Status), as the lot form
 * hides its Title section. A Category the form sets wins: after the save every
 * lot takes it. Otherwise the dry run's count decides, and an unknown selection
 * (no dry run yet, or nothing selected) keeps the rows.
 *
 * @param opts.categoryId The Category the form sets, or undefined ("Leave unchanged").
 * @param opts.review The latest dry run's counts, or null before it lands.
 * @returns True when the title rows apply to no lot.
 * @sideEffect none — pure.
 */
export function isEditLotsSelectionLand(opts: {
  categoryId: EditLotsFields['categoryId'];
  review: Pick<EditLotsReview, 'lotCount' | 'landLotCount'> | null;
}): boolean {
  const { categoryId, review } = opts;
  if (categoryId !== undefined) return isLandCategory(categoryId);
  return review != null && review.lotCount > 0 && review.landLotCount === review.lotCount;
}

/**
 * BL-1912 — the Edit lots price box's value as a `MoneyWire`, converted once at
 * the form's edge (`NumberInput` hands over the number it parsed from the typed
 * text). Keeps every typed digit —
 * no rounding — so the shared schema, not this converter, decides whether the
 * amount has too many decimals (AC 9).
 *
 * @param value The number the price box reports.
 * @param currency The selected lots' currency (the dry run's answer).
 * @returns The wire value.
 * @sideEffect none — pure.
 */
export function moneyWireFromInput(value: number, currency: string): MoneyWire {
  // A number parsed from typed text prints back as that text (`String` gives the
  // shortest round-trip form: 1234.5 → "1234.5", 1234.567 → "1234.567"). An
  // absurd magnitude prints in exponent form, which the schema refuses.
  return { amount: String(value), currency };
}

/**
 * BL-2243 — a stored price's `MoneyWire` (four-place storage scale, e.g.
 * `"1250.5000"`) as the form holds a typed one (`"1250.5"`): the trailing
 * fraction zeros dropped, exactly, by string — no float. Same currency.
 *
 * The lot editor seeds its price boxes from the detail payload with this, so
 * the box shows what the operator typed and an untouched save sends the same
 * text `moneyWireFromInput` would — a JPY `"7500.0000"` would otherwise trip
 * the save's minor-unit rule (BL-2131).
 *
 * @param wire The stored price as the detail payload carries it.
 * @returns The same amount and currency at the scale the form uses.
 * @sideEffect none — pure.
 */
export function moneyWireForInput(wire: MoneyWire): MoneyWire {
  const amount = wire.amount.includes('.') ? wire.amount.replace(/\.?0+$/, '') : wire.amount;
  return { amount, currency: wire.currency };
}

/**
 * BL-1912 — Sales Rep's first option (AC 1, AC 2): the rep every lot would follow
 * — "Default — Jane Smith", "Default — House" when that seller has no rep — or
 * "Default — each seller’s rep" when the lots' sellers differ.
 *
 * @param review The dry run's common seller and that seller's rep.
 * @returns The option label.
 * @sideEffect none — pure.
 */
export function editLotsSalesRepDefaultLabel(
  review: Pick<EditLotsReview, 'commonSellerId' | 'commonSellerSalesRep'>,
): string {
  if (review.commonSellerId == null) return 'Default — each seller’s rep';
  return `Default — ${review.commonSellerSalesRep?.name ?? HOUSE_SALES_REP_LABEL}`;
}

/**
 * BL-1912 — why Starting Price and Reserve can't be set on this selection
 * (AC 8), or null when they can. Before the first dry run lands there is no
 * reason to show; the rows stay disabled until the currency is known.
 *
 * @param review The dry run's answer, or null.
 * @returns The reason, or null.
 * @sideEffect none — pure.
 */
export function editLotsPriceRowsDisabledReason(
  review: Pick<EditLotsReview, 'pricingBasis' | 'currency'> | null,
): string | null {
  if (review == null) return null;
  if (review.pricingBasis === 'mixed') return PRICING_BASIS_MIXED_MESSAGE;
  if (review.currency == null) return PRICE_CURRENCY_MISMATCH_MESSAGE;
  return null;
}
